import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { PoSettlementActivityKind, PoSettlementDetail, PoSettlementDisplayStatus } from '../../../models/po-settlement/po-settlement';
import { PoSettlementProxyService } from '../../../proxies/po-settlement.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item-master.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { Translation } from '../../../models/Translations';
import { LocaleHelperService } from '../../../services/locale-helper.service';

@Component({
  selector: 'app-po-settlement-details',
  templateUrl: './po-settlement-details.component.html',
  styleUrl: './po-settlement-details.component.scss',
})
export class PoSettlementDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(PoSettlementProxyService);
  private readonly enumService = inject(EnumService);
  private readonly itemMasterService = inject(ItemMasterProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  private currencyLabels: Record<number, string> = {};
  private docStatusLabels: Record<number, string> = {};
  private paymentStatusLabels: Record<number, string> = {};
  private requestStatusLabels: Record<number, string> = {};

  readonly ActivityKind = PoSettlementActivityKind;

  isLoading = signal(0);
  enumsReady = signal(false);
  currentId = signal<string | null>(null);
  detail = signal<PoSettlementDetail | null>(null);
  headerTitle = signal('PO Settlement');

  constructor() {
    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
        this.docStatusLabels = this.enumService.getEnum('ApDocumentStatus').values as Record<number, string>;
        this.paymentStatusLabels = this.enumService.getEnum('ApPaymentStatus').values as Record<number, string>;
        this.requestStatusLabels = this.enumService.getEnum('ApPaymentRequestStatus').values as Record<number, string>;
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.buildBreadcrumb(null);
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.currentId.set(id);
      this.loadDetail(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private loadDetail(id: string): void {
    this.startLoading();
    this.service
      .getDetail(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const data = response?.data ?? null;
          this.detail.set(data);
          this.headerTitle.set(data?.purchaseOrderNumber ?? this.t('PoSettlement_Title', 'PO Settlement'));
          this.buildBreadcrumb(data?.purchaseOrderNumber ?? null);
          this.loadGoodsReceiptItemNames(data);
        },
        error: () => this.detail.set(null),
      });
  }

  /**
   * Item names for every code on the PO's goods receipts (#5235).
   *
   * ONE batch call for the distinct codes across all receipts — not one per line, and not one per
   * receipt. A PO with six GRNs of a dozen lines each is a single request either way, and the codes
   * repeat heavily between receipts of the same order.
   *
   * The GRN lines carry an `itemDisplayName` of their own, but it is frequently absent; this fills the
   * gap rather than replacing it, and the template still falls back to the bare code.
   */
  private loadGoodsReceiptItemNames(detail: PoSettlementDetail | null): void {
    this.itemNamesByCode.set(new Map());

    const codes = [
      ...new Set(
        (detail?.goodsReceipts ?? [])
          .flatMap((receipt) => receipt.lines ?? [])
          .map((line) => line.itemCode)
          .filter((code): code is string => !!code),
      ),
    ];

    if (codes.length === 0) return;

    this.itemMasterService.getByItemCodes(codes).subscribe({
      next: (response) => {
        const map = new Map<string, string>();
        for (const item of response?.data ?? []) {
          const name = this.locale.getLocalizedValue(item.itemDisplayName);
          if (item.itemCode && name) map.set(item.itemCode, name);
        }
        this.itemNamesByCode.set(map);
      },
      // A name is a nicety here; the code identifies the item on its own. Failing quietly keeps the
      // settlement figures on screen rather than taking the panel down over a lookup.
      error: () => this.itemNamesByCode.set(new Map()),
    });
  }

  /** Resolved item names by code. Empty until the batch above returns. */
  readonly itemNamesByCode = signal<Map<string, string>>(new Map());

  /**
   * How a goods-receipt line is labelled: the Item API's name, else the name the GRN carried, else
   * nothing — the template renders the code either way, so a missing name costs detail, not identity.
   */
  itemNameFor(itemCode: string, fallback?: Translation | null): string {
    return this.itemNamesByCode().get(itemCode) ?? this.locale.getLocalizedValue(fallback) ?? '';
  }

  private buildBreadcrumb(poNumber: string | null): void {
    this.breadcrumbService.set([
      { label: this.t('AccountsPayable', 'Accounts Payable') },
      { label: this.t('PoSettlement_Title', 'PO Settlement'), route: '/po-settlement' },
      { label: poNumber ?? '' },
    ]);
  }

  back(): void {
    this.router.navigate(['/po-settlement']);
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  docStatusLabel(status: number): string {
    return this.docStatusLabels[status] ?? '';
  }

  paymentStatusLabel(status: number): string {
    return this.paymentStatusLabels[status] ?? '';
  }

  requestStatusLabel(status: number): string {
    return this.requestStatusLabels[status] ?? '';
  }

  displayStatusLabel(status: PoSettlementDisplayStatus): string {
    switch (status) {
      case PoSettlementDisplayStatus.Settled:
        return this.t('PoSettlementStatus_Settled', 'Settled');
      case PoSettlementDisplayStatus.PartiallyPaid:
        return this.t('PoSettlementStatus_PartiallyPaid', 'Partially Paid');
      case PoSettlementDisplayStatus.PaymentRequested:
        return this.t('PoSettlementStatus_PaymentRequested', 'Payment Requested');
      case PoSettlementDisplayStatus.Unpaid:
        return this.t('PoSettlementStatus_Unpaid', 'Unpaid');
      default:
        return this.t('PoSettlementStatus_NoDocuments', 'No AP documents yet');
    }
  }

  displayStatusSeverity(status: PoSettlementDisplayStatus): 'success' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case PoSettlementDisplayStatus.Settled:
        return 'success';
      case PoSettlementDisplayStatus.PartiallyPaid:
        return 'warn';
      case PoSettlementDisplayStatus.PaymentRequested:
        return 'info';
      default:
        return 'secondary';
    }
  }

  /** Timeline icon per activity kind (#4346–#4350 added a third, so this is no longer a two-way ternary). */
  activityIcon(kind: PoSettlementActivityKind): string {
    switch (kind) {
      case PoSettlementActivityKind.PaymentPosted:
        return 'pi pi-check';
      case PoSettlementActivityKind.AdvancePosted:
        return 'pi pi-wallet';
      default:
        return 'pi pi-send';
    }
  }

  activityLabel(kind: PoSettlementActivityKind): string {
    switch (kind) {
      case PoSettlementActivityKind.PaymentPosted:
        return this.t('PoSettlement_PaymentPosted', 'Payment posted');
      case PoSettlementActivityKind.AdvancePosted:
        return this.t('PoSettlement_AdvancePosted', 'Advance posted');
      default:
        return this.t('PoSettlement_PaymentRequested', 'Payment requested');
    }
  }

  /** Settlement progress counts the advance DRAWN DOWN as settled, matching the API's SettlementPercent and
   * the Balance card — paid alone would under-report a PO largely settled by an advance. */
  paidPercent(): number {
    const d = this.detail();
    if (!d || d.invoiced <= 0) return 0;
    return Math.min(100, Math.round(((d.paid + d.advanceApplied) / d.invoiced) * 100));
  }
}
